import { useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

const labSchema = z.object({
  name: z.string().min(1, 'Lab name is required'),
  country: z.string().optional(),
  timeZone: z.string().optional(),
});

type LabFormData = z.infer<typeof labSchema>;

interface LabFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: LabFormData) => Promise<void>;
  initialData?: Partial<LabFormData>;
  title: string;
  isLoading?: boolean;
}

export function LabFormDialog({
  open, onClose, onSubmit, initialData, title, isLoading,
}: LabFormDialogProps) {
  const { control, handleSubmit, reset, formState: { errors } } = useForm<LabFormData>({
    resolver: zodResolver(labSchema),
    defaultValues: { name: '', country: '', timeZone: '' },
  });

  useEffect(() => {
    if (open) reset(initialData ?? { name: '', country: '', timeZone: '' });
  }, [open, initialData, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Lab Name"
                  required
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Country" fullWidth />
              )}
            />
            <Controller
              name="timeZone"
              control={control}
              render={({ field }) => (
                <TextField {...field} label="Timezone" fullWidth placeholder="e.g. Europe/Berlin" />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
